import React, { createContext, useState } from 'react';
import { createContextHook } from '../contextUtils';
import { useVisualization } from '../visualization/VisualizationContext';
import { Constants } from '../../constants/Variables';
import { CreateErrorNotification } from '../../components/common/Notifications/Notification';
import * as d3 from "d3";
import * as _ from "lodash";
import { useData } from "../../contexts/data/DataContext";
import { selectGeographies } from '../../service/parseServices';

// ChartContext manages the state for chart visualizations
const ChartContext = createContext();

export function ChartProvider({ children }) {
  // Controls the sorting order of chart data (ascending/descending)
  const [sorting, setSorting] = useState("desc");
  // Tracks which entities (states/districts) are currently visible in the chart
  const [visibleEntities, setVisibleEntities] = useState(null);
  // Maps entity IDs to their assigned colors for consistent visualization
  const [colorCodes, setColorCodes] = useState({});
  // Controls visibility of bar chart elements
  const [hiddenBar, setHiddenBar] = useState(false);
  // Toggles visibility of India-level data in charts
  const [hideIndia, setHideIndia] = useState(false);
  const [variableSource, setVariableSource] = useState("");
  
  const { visualization, view } = useVisualization();
  const { districtNames, stateNames, setGeographies } = useData();
  const color = d3
    .scaleOrdinal()
    .range([
      "#e41a1c", "#377eb8", "#4daf4a", "#984ea3",
      "#ff7f00", "#a65628", "#db5858", "#f781bf",
      "#db8658", "#F4A282",
    ]);

   /**
   * Toggles visibility of states/districts in visualization
   * Manages color assignments and entity limits
   * @param {string|number} id - Entity ID to toggle
   * @async
  */
   const toggleDisplayedStatesHandler = async (id) => {
    id = parseInt(id); // entity id
    let data = [];
    let visibleEntitiestemp = _.cloneDeep(visibleEntities); // making a deep cloned array of visible entities so that actual array dont get affected before processing
    let colorTemp = _.cloneDeep(colorCodes);
    if (visualization === Constants.Bar) {
      setHiddenBar(true);
    }
    /* if visibleEntities has not the matching entity id, it means the user is trying to see that particular entity
    but has to go through few checks */
    if (!visibleEntities.find((state) => state.id === parseInt(id))) {
      if (visualization === Constants.Line && visibleEntities.length >= 10) { // Since line visualization has a maximum limit of 10 lines
        CreateErrorNotification(
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "0.75rem",
              color: "#fff",
            }}
          >
            <h5 style={{ fontWeight: 700, color: "#fff" }}>
              Cannot display any more{" "}
              {view === Constants.State ? "states" : "districts"}
            </h5>
            <p style={{ color: "#fff" }}>
              A maximum of 10 states can be shown at a time. Please hide some
              {view === Constants.State ? "states" : "districts"} to allow
              others to be shown.
            </p>
          </div>
        );
      } else if (
        visualization === Constants.Bar &&
        visibleEntities.length >= 15
      ) { // Since bar visualization has a maximum limit of 15 lines
        CreateErrorNotification(
          <div
            className="toast-msg-div"
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "0.75rem",
              color: "#fff",
            }}
          >
            <h5 style={{ fontWeight: 700 }}>
              Cannot display any more{" "}
              {view === Constants.State ? "states" : "districts"}
            </h5>
            <p style={{ color: "#fff" }}>
              A maximum of 15 states can be shown at a time. Please hide some
              {view === Constants.State ? "states" : "districts"} to allow
              others to be shown.
            </p>
          </div>
        );
      } else {
        // makes sure that the new entity id is added to the deep cloned copy of visible entities
        await visibleEntities.forEach((state) => {
          if (view === Constants.State)
            data.push(state.id + "_" + stateNames[state.id]);
          else data.push(state.id + "_" + districtNames[state.id]);
        });
        if (view === Constants.State) data.push(id + "_" + stateNames[id]);
        else data.push(id + "_" + districtNames[id]);

        let temporary;
        for (let i = 0; i < 10; i++) {
          let flag = false;
          Object.keys(colorTemp).forEach((key) => {
            if (colorTemp[key] === color(i)) {
              flag = true;
              return;
            }
          });
          if (!flag) {
            temporary = color(i);
            break;
          }
        }
        if (id === 0) {
          setHideIndia(false);
        }
        visibleEntitiestemp.push({ id: id, color: temporary });
        colorTemp[id] = temporary;
      }
    } else { // if the entity id is already present in visible entities, then we have to remove it from the array
      await visibleEntities.forEach((state) => {
        if (state.id !== parseInt(id)) { // consider only ids that do not match with entity id
          if (view === Constants.State)
            data.push(state.id + "_" + stateNames[state.id]);
          else data.push(state.id + "_" + districtNames[state.id]);
        }
      });
      visibleEntitiestemp = visibleEntitiestemp.filter((t) => t.id !== parseInt(id));
      delete colorTemp[id]; // deletes that particular id entry from the cloned copy of colorcodes
      if (id === 0) {
        setHideIndia(true);
      }
    }
    if (data.length > 0) {
      let resp = await selectGeographies(data, view);
      setVisibleEntities(visibleEntitiestemp); // sets the updated cloned copy of visibleEntities
      setColorCodes(colorTemp); // sets the updated cloned copy of colorcodes
      setGeographies(resp["geographies"]);
    }
  };

  const value = {
    sorting, setSorting,
    visibleEntities, setVisibleEntities,
    colorCodes, setColorCodes,
    hiddenBar, setHiddenBar,
    hideIndia, setHideIndia,
    variableSource, setVariableSource,
    toggleDisplayedStatesHandler
  };

  return (
    <ChartContext.Provider value={value}>
      {children}
    </ChartContext.Provider>
  );
}

export const useChart = createContextHook(ChartContext, 'useChart');